import { useState, useEffect, useRef, useCallback } from "react";
import { MOOD_LABELS, ENERGY_LABELS } from "../utils/mood";
import { activationFor } from "../utils/activation";
import { readbackLine, ritualGreeting, ritualTimeChoices, MOOD_PHRASE } from "../utils/ritual";
import { detectCrisis, isAcuteReading } from "../lib/crisis";
import { setCommitment, formatTime } from "../utils/commitment";
import { hapticLight, hapticSuccess } from "../utils/haptics";
import CrisisSupport from "./CrisisSupport";
import "../styles/ritual.css";

/**
 * THE DAILY RITUAL
 * ---------------------------------------------------------------------------
 * Home used to be a dashboard: eight cards stacked vertically, every one of
 * them competing, none of them asking anything. A dashboard is a thing you
 * *read*. This is a thing you *do* — one question per screen, an answer that
 * changes the screen, and an end.
 *
 * The sequence is the whole product in ninety seconds:
 *
 *     mood  →  energy  →  here's what today asks for  →  when?  →  done
 *
 * Three things are load-bearing and should not be "simplified" away:
 *
 *   1. ONE QUESTION AT A TIME. The moment two questions share a screen the
 *      person is reading a form, and a form is something you put off.
 *
 *   2. THE SCREEN ANSWERS BACK. Choosing a mood re-lights the background before
 *      you've done anything for the app. That reciprocity is what separates a
 *      ritual from a survey, and it is the reason the mood tokens are wired to
 *      the sky rather than to a little coloured dot.
 *
 *   3. IT ENDS. Every step is escapable in one tap ("Later"), and the last step
 *      says so explicitly. A loop that cannot be closed is a loop people stop
 *      opening.
 *
 * The check-in is written the instant mood and energy are both known — the step
 * where the app starts talking, not the step where the person finishes. If they
 * walk away at "when?", the data that powers the evidence loop is already safe.
 */

const STEPS = ["mood", "energy", "response", "when", "close"];

/** Brightest first: read down the screen and the colour goes daylight → dusk. */
const MOOD_ORDER = [5, 4, 3, 2, 1];
const ENERGY_ORDER = [1, 2, 3, 4, 5];

function prefersReducedMotion() {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function Bloom() {
  return (
    <span className="rit-check" aria-hidden="true">
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
      </svg>
    </span>
  );
}

export default function DailyRitual({
  userId,
  date,
  greetingName,
  onSave,
  onSaveNote,
  onExit,
  onStart,
  showSupport = false,
}) {
  const [step, setStep] = useState("mood");
  const [mood, setMood] = useState(null);
  const [energy, setEnergy] = useState(null);
  const [resting, setResting] = useState(false);
  const [committed, setCommitted] = useState(null);
  const [chosen, setChosen] = useState(null);
  const [pickingTime, setPickingTime] = useState(false);
  const [customTime, setCustomTime] = useState(() => {
    const d = new Date();
    d.setHours(d.getHours() + 1, 0, 0, 0);
    return `${String(d.getHours()).padStart(2, "0")}:00`;
  });

  const [note, setNote] = useState("");
  const [showNote, setShowNote] = useState(false);
  const savedRef = useRef(false);
  const timer = useRef(null);
  const headingRef = useRef(null);
  const reduced = useRef(prefersReducedMotion());

  // The ritual owns the viewport while it is up; nothing behind it should scroll.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  useEffect(() => () => clearTimeout(timer.current), []);

  /**
   * The stage remounts on every step (key={step}), which drops focus to <body>
   * and strands a keyboard or screen-reader user at the top of the document
   * five times per ritual. Moving focus to the new heading restores the tab
   * position AND announces the step — which is why the stage no longer carries
   * aria-live, whose only effect was to re-read the entire screen each time.
   */
  useEffect(() => {
    headingRef.current?.focus();
  }, [step]);

  /**
   * Move on after a beat. The pause is not decoration: it is the window in
   * which the background finishes changing colour, so the person sees their
   * answer land before the next question replaces it. With reduced motion there
   * is nothing to wait for, so there is no wait.
   */
  const advance = useCallback((next, delay = 0) => {
    clearTimeout(timer.current);
    if (!delay || reduced.current) {
      setStep(next);
      return;
    }
    timer.current = setTimeout(() => setStep(next), delay);
  }, []);

  /**
   * Write the reading as soon as both halves exist — walking away at "when?"
   * must not lose it.
   *
   * The note cannot ride along: it is offered on this same screen, AFTER this
   * has already fired, so it used to be rendered, run past the crisis matcher,
   * and then silently discarded. It is now attached to the saved record by id
   * when the patient leaves the field.
   */
  const savedIdRef = useRef(null);
  useEffect(() => {
    if (step !== "response" || savedRef.current) return;
    if (mood == null || energy == null) return;
    savedRef.current = true;
    Promise.resolve(onSave?.({ mood, energy, note: null, context: "daily" }))
      .then((rec) => {
        if (rec?.id) savedIdRef.current = rec.id;
      })
      .catch(() => {});
  }, [step, mood, energy, onSave]);

  const commitNote = useCallback(() => {
    const text = note.trim();
    if (!savedIdRef.current) return;
    onSaveNote?.(savedIdRef.current, text || null);
  }, [note, onSaveNote]);

  const activation = mood != null && energy != null ? activationFor(mood, energy) : null;

  const chooseMood = (v) => {
    hapticLight();
    setMood(v);
    // At the bottom of the scale the sequence stops moving on its own. Support
    // has just appeared on this screen and auto-advancing would pull it away
    // after 620ms — the patient continues when they choose to.
    if (isAcuteReading(v)) return;
    advance("energy", 620);
  };

  const chooseEnergy = (v) => {
    hapticLight();
    setEnergy(v);
    advance("response", 620);
  };

  const commit = (time) => {
    hapticSuccess();
    const what = chosen?.label || "Something small";
    setCommitted(setCommitment(userId, { date, time, what }));
    setPickingTime(false);
    setStep("close");
  };

  const chooseRest = () => {
    hapticLight();
    setResting(true);
    setStep("close");
  };

  const stepIndex = STEPS.indexOf(step);
  const choices = ritualTimeChoices();

  // "Right now" means now: skip the dashboard entirely and open a session.
  const startsNow =
    committed?.time && (() => {
      const [h, m] = committed.time.split(":").map(Number);
      const now = new Date();
      return h * 60 + m <= now.getHours() * 60 + now.getMinutes() + 5;
    })();

  return (
    <main
      className="rit"
      id="main-content"
      tabIndex={-1}
      data-mood={mood ?? 0}
      data-step={step}
      aria-label="Today's check-in"
    >
      <div className="rit-sky" aria-hidden="true" />
      <div className="rit-bloom" aria-hidden="true" />

      <div className="rit-chrome">
        <ol className="rit-dots" aria-hidden="true">
          {STEPS.map((s, i) => (
            <li key={s} className="rit-dot" data-on={i <= stepIndex} />
          ))}
        </ol>
        {/* One word for one action, in one place, on every screen. It was
            "Later" / "Close" / "Skip" across the two sequences — three labels
            for the same escape, which teaches a patient that the top-right
            corner is unpredictable. */}
        <button
          type="button"
          className="rit-skip"
          onClick={() => {
            commitNote();
            onExit?.();
          }}
        >
          Later
        </button>
      </div>

      <div className="rit-stage" key={step}>
        {/* ── 1. Mood ─────────────────────────────────────────────────── */}
        {step === "mood" && (
          <>
            <header className="rit-head">
              <p className="rit-eyebrow">{ritualGreeting()}{greetingName ? `, ${greetingName}` : ""}</p>
              <h1 className="rit-q" ref={headingRef} tabIndex={-1}>How are you, really?</h1>
              <p className="rit-sub">No wrong answer, and no streak riding on it.</p>
            </header>

            {/* The bottom of the scale gets support HERE, on the tap, not two
                screens and 1.2 seconds later. The reading most likely to be
                abandoned mid-sequence is exactly the one that must not have to
                survive two more screens to be met with anything. */}
            {isAcuteReading(mood) && <CrisisSupport variant="crisis" />}

            <div className="rit-moods" role="group" aria-label="Mood">
              {MOOD_ORDER.map((v) => (
                <button
                  key={v}
                  type="button"
                  className="rit-mood"
                  data-mood={v}
                  aria-pressed={mood === v}
                  onClick={() => chooseMood(v)}
                >
                  <span className="rit-orb" data-mood={v} aria-hidden="true" />
                  <span className="rit-mood-label">{MOOD_LABELS[v - 1]}</span>
                </button>
              ))}
            </div>

            {isAcuteReading(mood) && (
              <div className="rit-actions">
                <button className="btn btn-primary btn-full" onClick={() => setStep("energy")}>
                  Carry on
                </button>
              </div>
            )}
          </>
        )}

        {/* ── 2. Energy ───────────────────────────────────────────────── */}
        {step === "energy" && (
          <>
            <header className="rit-head">
              {/* The screen says the answer back before it asks the next
                  question. "Noted" is an acknowledgement of receipt from a
                  form; naming the day is the app having heard them. */}
              <p className="rit-eyebrow">{MOOD_PHRASE[mood - 1]}</p>
              <h1 className="rit-q" ref={headingRef} tabIndex={-1}>And how much is in the tank?</h1>
              <p className="rit-sub">
                Low and flat is a different day from low and restless. They
                need opposite things.
              </p>
            </header>

            <div className="rit-energy" role="group" aria-label="Energy">
              {ENERGY_ORDER.map((v) => (
                <button
                  key={v}
                  type="button"
                  className="rit-energy-opt"
                  aria-pressed={energy === v}
                  aria-label={`Energy: ${ENERGY_LABELS[v - 1]}`}
                  onClick={() => chooseEnergy(v)}
                >
                  <span className="rit-bar" aria-hidden="true">
                    <span className="rit-bar-fill" data-step={v} />
                  </span>
                  <span className="rit-energy-label">{ENERGY_LABELS[v - 1]}</span>
                </button>
              ))}
            </div>
          </>
        )}

        {/* ── 3. What today asks for ──────────────────────────────────
            The payoff and a decision in one screen. Reading a prescription is
            passive and leaves a hole where the action should be; picking from
            three already-decided options is a choice a person at mood 1 can
            still make, and it is the choice behavioral activation actually
            asks them to make. */}
        {step === "response" && (
          <>
            <header className="rit-head">
              <p className="rit-eyebrow">Today</p>
              <h1 className="rit-readback" ref={headingRef} tabIndex={-1}>{readbackLine(mood, energy)}</h1>
            </header>

            {/* SAFETY, in two independent paths.
                1. ACUTE: a reading at the bottom of the mood scale surfaces
                   support on that single reading. The sustained rule needs
                   seven consecutive days and seven stored check-ins, so it is
                   mathematically dead for a new patient's entire first week —
                   exactly when a freshly-referred patient is most likely to be
                   acutely unwell.
                2. TEXT: the deterministic matcher in lib/crisis.js was
                   unreachable from either sequence, because neither had a text
                   field. The note below is optional and one tap away, and it
                   is the only thing detectCrisis() can read. */}
            {(isAcuteReading(mood) || showSupport) && (
              <CrisisSupport variant={isAcuteReading(mood) ? "crisis" : "sustained"} />
            )}

            <div className="rit-offer">
              <p className="rit-offer-head">{activation?.headline}</p>
              <p className="rit-offer-why">{activation?.why}</p>
            </div>

            <div className="rit-choices" role="group" aria-label="What you'll do">
              {(activation?.options || []).map((o) => (
                <button
                  key={o.id}
                  type="button"
                  className="rit-choice"
                  onClick={() => {
                    hapticLight();
                    commitNote();
                    setChosen(o);
                    setStep("when");
                  }}
                >
                  <span className="rit-choice-label">{o.label}</span>
                  <span className="rit-choice-time">{o.minutes} min</span>
                </button>
              ))}
            </div>

            {showNote ? (
              <div className="rit-note">
                <label className="rit-note-label" htmlFor="rit-note-field">
                  Anything you want on the record?
                </label>
                <textarea
                  id="rit-note-field"
                  className="rit-note-field"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  onBlur={commitNote}
                  placeholder="However today has actually been."
                  rows={3}
                />
                {detectCrisis(note) && <CrisisSupport variant="crisis" />}
              </div>
            ) : (
              <button
                type="button"
                className="rit-quiet rit-quiet--inline"
                onClick={() => setShowNote(true)}
              >
                Something else going on?
              </button>
            )}

            <div className="rit-actions">
              <button type="button" className="rit-quiet" onClick={chooseRest}>
                Not today
              </button>
            </div>
          </>
        )}

        {/* ── 4. When ─────────────────────────────────────────────────── */}
        {step === "when" && (
          <>
            <header className="rit-head">
              <p className="rit-eyebrow">{chosen?.label}</p>
              <h1 className="rit-q" ref={headingRef} tabIndex={-1}>When will you do it?</h1>
              <p className="rit-sub">
                A named time is much easier to keep than a vague one. Pick
                whichever you&apos;d actually be free at.
              </p>
            </header>

            <div className="rit-choices">
              {choices.map((c) => (
                <button
                  key={c.key}
                  type="button"
                  className="rit-choice"
                  onClick={() => commit(c.time)}
                >
                  <span className="rit-choice-label">{c.label}</span>
                  {c.key !== "now" && (
                    <span className="rit-choice-time">{formatTime(c.time)}</span>
                  )}
                </button>
              ))}

              {pickingTime ? (
                <div className="rit-picker">
                  <input
                    type="time"
                    className="rit-time-input"
                    value={customTime}
                    onChange={(e) => setCustomTime(e.target.value)}
                    aria-label="What time"
                  />
                  <button className="btn btn-primary" onClick={() => commit(customTime)}>
                    Set it
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className="rit-choice rit-choice--quiet"
                  onClick={() => setPickingTime(true)}
                >
                  <span className="rit-choice-label">Another time</span>
                </button>
              )}
            </div>

            <button type="button" className="rit-quiet" onClick={chooseRest}>
              Not today
            </button>
          </>
        )}

        {/* ── 5. Close ────────────────────────────────────────────────── */}
        {step === "close" && (
          <div className="rit-close">
            <Bloom />
            {resting ? (
              <>
                <h1 className="rit-q" ref={headingRef} tabIndex={-1}>Resting today.</h1>
                <p className="rit-sub">
                  That counts too. You still showed up and said how you were —
                  that&apos;s the part the rest is built on.
                </p>
              </>
            ) : (
              <>
                <h1 className="rit-q" ref={headingRef} tabIndex={-1}>That&apos;s today, set.</h1>
                <p className="rit-plan">
                  <span className="rit-plan-time">{formatTime(committed?.time)}</span>
                  <span className="rit-plan-what">{committed?.what}</span>
                </p>
                <p className="rit-sub">
                  {/* The app does not learn: activationFor() is a pure function
                      of two integers. The record is what accumulates, and it is
                      the patient's — so that is what the sentence says. */}
                  You&apos;ll be asked how you are afterwards. Both readings go in
                  your record, and the record is what shows you the pattern.
                </p>
              </>
            )}

            <div className="rit-actions">
              {!resting && startsNow && onStart && (
                <button
                  className="btn btn-primary btn-full"
                  onClick={() => {
                    onStart();
                    onExit?.();
                  }}
                >
                  Start now
                </button>
              )}
              <button type="button" className="rit-quiet" onClick={onExit}>
                See the rest of today
              </button>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
